import React from "react";
import { Link } from "react-router-dom";
import { Check, Lock, ArrowRight, Hourglass, MessageSquareText } from "lucide-react";
import { CAL_STEPS, calStage, formatHeldDate } from "../lib/calibrationStage";

function Steps({ stage }) {
  return (
    <ol className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cal-tracker-steps">
      {CAL_STEPS.map((label, i) => {
        const done = stage.done[i];
        const active = !done && i === stage.current;
        return (
          <li
            key={label}
            className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors ${done ? "border-[#00A36C] bg-[#00A36C]/10" : active ? "border-[#FF4F00] bg-[#FF4F00]/10" : "border-[#111]/20 text-[#666]"}`}
            data-testid={`cal-tracker-step-${i}`}
            data-state={done ? "done" : active ? "active" : "todo"}
          >
            <span className={`w-5 h-5 shrink-0 flex items-center justify-center font-mono-cal text-[10px] ${done ? "bg-[#00A36C] text-white" : active ? "bg-[#FF4F00] text-white" : "border border-[#111]/30"}`}>
              {done ? <Check size={12} /> : i + 1}
            </span>
            <span className="font-medium leading-tight">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

const scrollToConversation = () =>
  document.getElementById("conversation-card")?.scrollIntoView({ behavior: "smooth", block: "center" });

function NextAction({ cal, stage, onDetailPage }) {
  const first = (cal.employee_name || "the employee").split(" ")[0];
  const compareLink = (ghost) => (
    <Link to={`/calibrations/${cal.id}/comparison`} className={`cal-btn ${ghost ? "cal-btn-ghost" : ""}`} data-testid={ghost ? "cal-tracker-compare" : "cal-tracker-cta"}>
      View comparison <ArrowRight size={14} />
    </Link>
  );
  if (stage.held) {
    return (
      <Action icon={<Check size={14} className="text-[#00A36C] shrink-0" />} text={`Conversation with ${first} held on ${formatHeldDate(cal.conversation_held_on)}. This calibration is complete.`}>
        {compareLink(true)}
      </Action>
    );
  }
  if (stage.complete) {
    return (
      <Action text={`Both ratings are in. Review the gaps and the AI coaching, hold the conversation with ${first}, then mark it as held.`}>
        <div className="flex flex-wrap gap-2">
          {compareLink(false)}
          {onDetailPage ? (
            <button onClick={scrollToConversation} className="cal-btn cal-btn-ghost" data-testid="cal-tracker-mark-held"><MessageSquareText size={14} /> Mark conversation held</button>
          ) : (
            <Link to={`/calibrations/${cal.id}`} className="cal-btn cal-btn-ghost" data-testid="cal-tracker-mark-held"><MessageSquareText size={14} /> Mark conversation held</Link>
          )}
        </div>
      </Action>
    );
  }
  if (!stage.mgr) {
    return (
      <Action text={stage.emp ? `${first} has submitted. Your blind rating is the last step before the comparison unlocks.` : `Rate ${first} now — your rating stays sealed until ${first} submits too.`}>
        <Link to={`/calibrations/${cal.id}/rate`} className="cal-btn" data-testid="cal-tracker-cta"><Lock size={14} /> Rate now (blind)</Link>
      </Action>
    );
  }
  return (
    <Action icon={<Hourglass size={14} className="text-[#FF4F00] shrink-0" />} text={`Waiting for ${first}'s self-rating. ${onDetailPage ? "Copy or resend the invite link below if they need a nudge." : "Open the calibration to copy or resend the invite link."}`}>
      {!onDetailPage && <Link to={`/calibrations/${cal.id}`} className="cal-btn cal-btn-ghost" data-testid="cal-tracker-cta">Open calibration <ArrowRight size={14} /></Link>}
    </Action>
  );
}

function Action({ text, icon, children }) {
  return (
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-[#111]/15 pt-4">
      <p className="text-sm text-[#111] flex items-start gap-2" data-testid="cal-tracker-next-text">{icon}{text}</p>
      {children}
    </div>
  );
}

export const CalibrationTracker = ({ cal, onDetailPage = false, title }) => {
  const stage = calStage(cal);
  return (
    <div className="cal-card p-5 md:p-6" data-testid="cal-tracker">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ NEXT STEP ]{title ? ` · ${title}` : ""}</div>
        <span className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]" data-testid="cal-tracker-status">{stage.label}</span>
      </div>
      <Steps stage={stage} />
      <NextAction cal={cal} stage={stage} onDetailPage={onDetailPage} />
    </div>
  );
};

export default CalibrationTracker;
